import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Input — 입력 필드. spec §10.
 * 1px border-strong / radius-md / padding 13px 14px.
 * Focus 시 border primary + 4px ring(rgba(49,130,246,0.12)).
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, label, hint, error, leadingIcon, trailingIcon, id, ...rest },
    ref,
  ) => {
    const inputId = id ?? rest.name
    const hasError = Boolean(error)
    return (
      <label htmlFor={inputId} className="block">
        {label && (
          <span className="mb-1.5 block text-[13px] font-semibold text-text-2">
            {label}
          </span>
        )}
        <span
          className={cn(
            'relative flex items-center w-full bg-surface',
            'rounded-md border border-border-strong',
            'transition-shadow transition-colors duration-150',
            hasError
              ? 'border-error focus-within:shadow-[0_0_0_4px_rgba(240,68,82,0.12)]'
              : 'focus-within:border-primary focus-within:shadow-[0_0_0_4px_rgba(49,130,246,0.12)]',
          )}
        >
          {leadingIcon && (
            <span className="pl-3.5 text-text-3 flex items-center">{leadingIcon}</span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'flex-1 bg-transparent outline-none',
              'px-3.5 py-3 text-[14px] text-text placeholder:text-text-3',
              leadingIcon && 'pl-2',
              trailingIcon && 'pr-2',
              className,
            )}
            {...rest}
          />
          {trailingIcon && (
            <span className="pr-3.5 text-text-3 flex items-center">{trailingIcon}</span>
          )}
        </span>
        {(hint || error) && (
          <span
            className={cn(
              'mt-1.5 block text-[12px]',
              hasError ? 'text-error' : 'text-text-3',
            )}
          >
            {error ?? hint}
          </span>
        )}
      </label>
    )
  },
)
Input.displayName = 'Input'
